import React, { useState, useEffect, useMemo, useCallback } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db, storage } from "./firebase";
import { doc, getDoc, onSnapshot, deleteDoc, collection, query, where, getDocs } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { seedDemoData } from "./utils/seedDemoData"; // [NEW] Demo Data Seeder

// Components
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import Login from "./components/Login";
import SplashScreen from "./components/SplashScreen";
import NaverCallbackLoading from "./components/NaverCallbackLoading";
import ProductTour from "./components/ProductTour"; // [NEW] Tour Component
import ProfileEditModal from "./components/ProfileEditModal";
import AaronWidget from "./components/AaronWidget";
import OrganizationSetup from "./components/OrganizationSetup"; // [NEW] Organization Setup
import ErrorBoundary from "./components/ErrorBoundary"; // [NEW]
import MinistryReportModal from "./components/MinistryReportModal"; // [NEW] 보고서 모달 추가
import LogoutConfirmModal from "./components/LogoutConfirmModal";
import MobileSidebarPortal from "./components/MobileSidebarPortal";

import BottomNav from "./components/BottomNav";
import { getCenterActionContent, getHeaderActionContent } from "./app/headerActionContent";
import { buildMinistryReportStats } from "./app/reportStats";
import { getBottomPaddingClass, getContentContainerClass, getMainPaddingTop } from "./app/layoutClassNames";
import { createDataMutationActions } from "./app/dataMutationActions";
import { runDailyAutoNotifications } from "./app/autoNotifications";
import { cleanupGuestData } from "./app/guestCleanup";
import { updateProfileWithMigration } from "./app/profileUpdate";
import { createSocialAuthHandlers } from "./app/socialAuthHandlers";
import { createBasicHandlers } from "./app/basicHandlers";
import { createTourHandlers } from "./app/tourHandlers";
import { renderMainContent } from "./app/mainContentRenderer";
import { createNaverMessageHandler, runNaverRedirectCheck } from "./app/naverAuthEffects";

// Hooks
import useData from "./hooks/useData";
import { useNotification } from "./contexts/NotificationContext";
import { useModal } from "./contexts/ModalContext";
import useAchievements from "./hooks/useAchievements";
import useCheer from "./hooks/useCheer"; // [NEW] 격려 시스템
import { useAaronInsights } from "./hooks/useAaronInsights"; // [NEW] 아론 자동 제안

// [REFACTORED] 테마 설정 분리
import { getThemeForDepartment } from "./constants/theme";
import BadgeModal from "./components/BadgeModal";
import CheerModal from "./components/CheerModal"; // [NEW] 격려 모달
import CheerReceivedModal from "./components/CheerReceivedModal"; // [NEW] 격려 받음 모달
import StudentDetailModal from "./views/student/StudentDetailModal";
import TeacherDetailModal from "./views/teacher/TeacherDetailModal";
import { ICONS as STUDENT_ICONS, FAITH_STATE_STYLES as STUDENT_FAITH_STATE_STYLES } from "./views/student/constants";
import MobileDetailView from "./views/mobile/MobileDetailView";
import { MOBILE_LIST_ICONS, buildDepartmentOptions, getDeptColorClass } from "./views/mobile/mobileListUtils";
import { shouldMaskTeacherInfoMobile } from "./utils/roleUtils";

// [NEW] Social Login Utils
import { initializeKakao, handleNaverCallback, initializeNaver } from "./utils/socialLogin";

const createEmptyNotificationStudentForm = () => ({
    name: "",
    gender: "남",
    phone: "",
    grade: "",
    class: "",
    school: "",
    guardian1Name: "",
    guardian1Phone: "",
    guardian2Name: "",
    guardian2Phone: "",
    birth: "",
    address: "",
    faithState: "기존신자",
    notes: "",
    photoUrl: "",
    prayerTopic: "",
});

const createEmptyNotificationTeacherForm = () => ({
    name: "",
    gender: "남",
    phone: "",
    grade: "",
    birth: "",
    address: "",
    notes: "",
    role: "교사",
    charge: "",
    photoUrl: "",
});

export default function App() {
    // [NEW] Splash Screen State (Toss Style)
    const [showSplash, setShowSplash] = useState(true);
    // [NEW] Product Tour State
    const [showTour, setShowTour] = useState(false);
    // [NEW] Tour Demo Action State
    const [tourAction, setTourAction] = useState(null);

    const [pendingSocialData, setPendingSocialData] = useState(null); // [NEW] Social Signup State

    // [FIX] 네이버 콜백 감지: URL에 access_token이 있으면 네이버 처리 중으로 간주
    // useState 초기값으로 바로 설정하여 렌더링 전부터 상태를 잡음
    const [isNaverCallback, setIsNaverCallback] = useState(window.location.href.includes("access_token"));

    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null); // { churchName, deptName, name }
    const [activeMenu, setActiveMenu] = useState("dashboard");
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [appReady, setAppReady] = useState(false); // New state for transitions

    // UI States
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [isDashboardSettingsOpen, setIsDashboardSettingsOpen] = useState(false);
    const [isAaronOpen, setIsAaronOpen] = useState(false);
    const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false); // [NEW] Logout Modal State

    // [NEW] Swipe State for Mobile Menu
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const minSwipeDistance = 150;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onMenuSwipeEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isRightSwipe) {
            setIsMobileMenuOpen(false);
        }
    };

    // Sub-view Navigation States
    const [studentToEdit, setStudentToEdit] = useState(null);
    const [targetDate, setTargetDate] = useState(null); // For Attendance Navigation
    const [targetStudentId, setTargetStudentId] = useState(null); // For Counseling Navigation
    const [targetTeacherId, setTargetTeacherId] = useState(null); // For Teacher Detail Navigation
    const [targetStatisticsSection, setTargetStatisticsSection] = useState(null); // For Statistics Deep-link
    const [detailReturnMenu, setDetailReturnMenu] = useState(null); // For Deep-link Detail Back Navigation
    const [targetLogId, setTargetLogId] = useState(null); // For Counseling Navigation
    const [notificationStudentDetail, setNotificationStudentDetail] = useState(null);
    const [notificationTeacherDetail, setNotificationTeacherDetail] = useState(null);
    const [studentListType, setStudentListType] = useState('student'); // [NEW] Lifted state for Student/Teacher toggle
    const [studentSearchTerm, setStudentSearchTerm] = useState(""); // [NEW] Lifted state for Student Search
    const [attendanceSearchQuery, setAttendanceSearchQuery] = useState(""); // [NEW] Lifted state for Attendance Search
    const [attendanceTargetGroup, setAttendanceTargetGroup] = useState("students"); // [NEW] Lifted state for Attendance Toggle
    const [counselingSearchQuery, setCounselingSearchQuery] = useState(""); // [NEW] Lifted state for Counseling Search
    const [counselingTargetGroup, setCounselingTargetGroup] = useState("students"); // [NEW] Lifted state for Counseling Toggle

    // [NEW] Multi-Department Support
    const [selectedDepartment, setSelectedDepartment] = useState("All");

    // Theme Management
    const [isStatsEditMode, setIsStatsEditMode] = useState(false); // [NEW] Statistics Edit Mode Lifted State
    const [isStatsSettingsOpen, setIsStatsSettingsOpen] = useState(false); // [NEW] Statistics Settings Modal State
    const [isReportOpen, setIsReportOpen] = useState(false); // [NEW] Dashboard Report state

    // [NEW] Mobile Detection for App-level routing
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // [NEW] Auto-select single department when user has only one department
    useEffect(() => {
        if (userProfile?.deptName) {
            const hasExtraDepts = userProfile?.myDepartments && userProfile.myDepartments.filter(d => d !== userProfile.deptName).length > 0;
            if (!hasExtraDepts && selectedDepartment === 'All') {
                // Only one department - auto-select it
                setSelectedDepartment(userProfile.deptName);
            }
        }
    }, [userProfile?.deptName, userProfile?.myDepartments]);

    // [NEW] Notification Context
    const { addNotification, initNotifications } = useNotification();
    const { openAlert, openConfirm } = useModal();

    // [NEW] Achievement System - 배지 시스템
    const {
        incrementStat,
        checkAndAwardBadges,
        earnedBadges,
        totalPoints,
        level,
        levelProgress,
        newBadge,
        showBadgeModal,
        closeBadgeModal,
        syncWithExistingData // [NEW] 기존 데이터 동기화 함수
    } = useAchievements(userProfile);

    // [NEW] 격려 시스템
    const { sendCheer, receivedCheers, markAsRead } = useCheer(userProfile);
    const [isCheerModalOpen, setIsCheerModalOpen] = useState(false);
    const [cheerTargetUser, setCheerTargetUser] = useState(null);

    // [NEW] 격려 받음 모달 상태
    const [showCheerReceivedModal, setShowCheerReceivedModal] = useState(false);
    const [currentReceivedCheer, setCurrentReceivedCheer] = useState(null);
    const [processedCheerIds, setProcessedCheerIds] = useState(new Set());
    const [notificationTeacherDetailPos] = useState({ x: 0, y: 0 });
    const [notificationTeacherLogInputContent, setNotificationTeacherLogInputContent] = useState("");
    const [notificationTeacherLogInputType, setNotificationTeacherLogInputType] = useState("면담");
    const [notificationTeacherLogInputDate, setNotificationTeacherLogInputDate] = useState(new Date().toISOString().split('T')[0]);
    const [notificationTeacherLogInputSentiment, setNotificationTeacherLogInputSentiment] = useState("neutral");
    const [notificationShowAllLogs, setNotificationShowAllLogs] = useState(false);
    const [notificationEditingLogId, setNotificationEditingLogId] = useState(null);
    const [, setIsNotificationCreatingLog] = useState(false);
    const [isNotificationDetailEditing, setIsNotificationDetailEditing] = useState(false);
    const [notificationFormData, setNotificationFormData] = useState(createEmptyNotificationStudentForm);
    const [notificationTeacherFormData, setNotificationTeacherFormData] = useState(createEmptyNotificationTeacherForm);
    const [isNotificationDetailDeptOpen, setIsNotificationDetailDeptOpen] = useState(false);
    const [, setIsNotificationUploading] = useState(false);
    const [notificationShowLinkPopup, setNotificationShowLinkPopup] = useState(false);
    const [notificationTeamMembers, setNotificationTeamMembers] = useState([]);
    const [notificationLinkLoading, setNotificationLinkLoading] = useState(false);
    const [notificationTouchStart, setNotificationTouchStart] = useState(null);
    const [notificationTouchStartY, setNotificationTouchStartY] = useState(null);
    const [notificationTouchEnd, setNotificationTouchEnd] = useState(null);
    const [notificationTouchEndY, setNotificationTouchEndY] = useState(null);

    // 격려 모달 열기 핸들러
    const handleOpenCheerModal = (targetUser) => {
        setCheerTargetUser(targetUser);
        setIsCheerModalOpen(true);
    };

    // 격려 보내기 핸들러
    const handleSendCheer = async (cheerData) => {
        await sendCheer(cheerData);
        // 격려 보내기 배지 통계 증가
        incrementStat('cheer_sent_count', 1);
        checkAndAwardBadges();
    };

    // [NEW] 새로운 격려 감지 및 모달 표시
    useEffect(() => {
        if (receivedCheers && receivedCheers.length > 0) {
            // 안 읽은 격려 중 아직 처리하지 않은 것 찾기
            const unreadCheer = receivedCheers.find(
                cheer => !cheer.isRead && !processedCheerIds.has(cheer.id)
            );

            if (unreadCheer && !showCheerReceivedModal) {
                setCurrentReceivedCheer(unreadCheer);
                setShowCheerReceivedModal(true);
                // 처리된 격려 ID 추가
                setProcessedCheerIds(prev => new Set([...prev, unreadCheer.id]));
            }
        }
    }, [receivedCheers, showCheerReceivedModal, processedCheerIds]);

    // [NEW] 격려 받음 모달 닫기 핸들러
    const handleCloseCheerReceivedModal = () => {
        setShowCheerReceivedModal(false);
        setCurrentReceivedCheer(null);
    };

    // [NEW] Effect to sync notifications with organizationId
    useEffect(() => {
        if (userProfile?.organizationId) {
            initNotifications(userProfile.organizationId);
        }
    }, [userProfile?.organizationId, initNotifications]);

    // [NEW] 기존 데이터로 배지 통계 동기화 (1회만 실행)
    useEffect(() => {
        // 데이터가 로드되고 사용자가 로그인된 상태에서만 동기화
        if (students.length > 0 || teachers.length > 0 || visitLogs.length > 0) {
            syncWithExistingData({ students, teachers, visitLogs, schedules, finances });
        }
    }, [userProfile?.organizationId]); // organizationId가 변경될 때만 실행 (1회)

    // Auth & Profile Listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (u) => {
            if (u) {
                // [MOD] Cross-device tour state: Check firestore instead of localStorage
                // We'll check this inside the docSnap.exists() block below

                try {
                    const docRef = doc(db, "users", u.uid);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists() && Object.keys(docSnap.data()).length > 0) {
                        const data = docSnap.data();

                        if (data.churchName && data.setupComplete) {
                            // [CASE 1] Existing, Complete User with Organization
                            setUser(u);
                            setUserProfile({ ...data, uid: u.uid, id: u.uid });


                            if (data.organizationId) {
                                initNotifications(data.organizationId);
                            }

                            seedDemoData(u);

                            const isMobileDevice = window.innerWidth < 768;
                            const isAdminUser = data.memberRole === 'admin';
                            if (!data.hasSeenTour && !isMobileDevice && isAdminUser) {
                                setShowTour(true);
                            }

                            setAppReady(true);
                        } else if (data.churchName && !data.setupComplete) {
                            // [CASE 1.5] Has profile but needs organization setup
                            setUser(u);
                            setUserProfile(data);
                        } else {
                            // [CASE 2] Data exists but invalid/incomplete -> Treat as new or partial
                            // Actually, if it has data keys but no churchName, it's effectively a new user setup flow
                            setUser(u);
                        }
                    } else {
                        // [CASE 3] User document does NOT exist OR is empty (Ghost Document)
                        // Treat as completely new user
                        if (docSnap.exists()) {
                            console.log("👻 빈 유령 문서 감지됨. 삭제 후 신규 가입 진행.");
                            try { await deleteDoc(docRef); } catch (e) { /* ignore */ }
                        }

                        setUser(u);
                        const providerId = u.providerData[0]?.providerId || 'social';

                        // [FIX] 문서가 없으면 구글뿐만 아니라 모든 소셜 로그인(카카오, 네이버 등)에 대해 가입 모드로 전환
                        // 단, 이메일 가입(password)과 익명(게스트) 로그인은 제외
                        if (!pendingSocialData && providerId !== 'password' && !u.isAnonymous) {
                            // 소셜 로그인인데 프로필이 없는 경우 -> 회원가입 2단계 진행
                            console.log(`[AUTH] 신규 소셜 회원 감지 (${providerId}). 추가 정보 입력 모드 진입.`);
                            setPendingSocialData({
                                email: u.email,
                                name: u.displayName,
                                provider: providerId.includes('kakao') ? 'kakao' : (providerId.includes('naver') ? 'naver' : 'google')
                            });
                        }
                    }

                } catch (e) { console.error("프로필 로드 실패", e); }

                // [NEW] Guest Profile Load
                if (u.isAnonymous && !pendingSocialData) {
                    const savedProfile = localStorage.getItem("guest_userProfile");
                    if (savedProfile) {
                        setUserProfile(JSON.parse(savedProfile));
                        setAppReady(true);
                    }
                }

            } else {
                setUser(null);
                setUserProfile(null);
                setPendingSocialData(null);
                setShowTour(false);
                setAppReady(false);
            }

            // [FIX] 네이버 콜백 처리 중일 때는 여기서 로딩을 끄지 않음 (checkNaver에서 끔)
            if (!isNaverCallback) {
                setLoading(false);
            }
        });
        return () => unsubscribe();
    }, [isNaverCallback]);

    // [NEW] Real-time Profile Monitor (Cross-device Sync)
    // 다른 기기에서 탈퇴하거나 정보가 변경되면 즉시 반영
    // [FIX] appReady가 true일 때만(즉, 로그인이 완전히 끝난 후) 감시해야 재가입 시 오작동 안 함
    useEffect(() => {
        if (!user || user.isAnonymous || !appReady) return;

        const unsubProfile = onSnapshot(doc(db, "users", user.uid), (docSnap) => {
            if (!docSnap.exists()) {
                // Case: Account deleted from another device
                // Only trigger if we were previously logged in (user exists)
                console.log("URGENT: User profile disappeared. Force logout.");
                signOut(auth).then(() => {
                    openAlert("계정 정보가 삭제되어 로그아웃되었습니다.", "알림");
                    window.location.reload();
                });
            } else {
                // Case: Profile updated on another device (Sync)
                // Update local state to match remote
                const newData = docSnap.data();
                // Compare critical fields to avoid unnecessary re-renders loop if we strictly check objects
                // But generally safe to just set it if we want real-time sync
                setUserProfile(prev => {
                    if (JSON.stringify(prev) !== JSON.stringify(newData)) {
                        return { ...newData, uid: user.uid, id: user.uid };

                    }
                    return prev;
                });
            }
        });

        return () => unsubProfile();
    }, [user, openAlert]);

    // [REFACTORED] 🎨 테마 로직 분리됨 → src/constants/theme.js
    // 기존 30줄+ 코드가 한 줄로 대체됨!


    const currentTheme = getThemeForDepartment(selectedDepartment, userProfile);

    // Data Hooks - [MOD] Pass userProfile for organization-based queries
    const {
        students, teachers, schedules, finances, annualBudget,
        visitLogs, meetingMinutes, chatHistory, setChatHistory, attendanceLogs,
        addItem: rawAddItem, updateItem: rawUpdateItem, deleteItem: rawDeleteItem, handleUpdateAnnualBudget,
        myDepartments, handleUpdateMyDepartments, refreshAttendanceLogs
    } = useData(user, userProfile, loading, activeMenu);

    // [NEW] 아론 자동 제안 시스템 가동 (데이터 로드 후 실행)
    useAaronInsights(students, userProfile);

    const {
        addItem,
        bulkAddItem,
        updateItem,
        deleteItem,
        bulkDeleteItem
    } = useMemo(
        () => createDataMutationActions({
            rawAddItem,
            rawUpdateItem,
            rawDeleteItem,
            openAlert,
            addNotification,
            userProfile,
            user,
            incrementStat,
            checkAndAwardBadges,
            students
        }),
        [
            rawAddItem,
            rawUpdateItem,
            rawDeleteItem,
            openAlert,
            addNotification,
            userProfile,
            user,
            incrementStat,
            checkAndAwardBadges,
            students
        ]
    );

    // [NEW] Splash Screen Logic
    useEffect(() => {
        // App is ready when auth is checked (loading=false)
        if (!loading) {
            // Add a small artificial delay to let the animation play out comfortably
            const timer = setTimeout(() => {
                setShowSplash(false);
            }, 1200); // 1.2 seconds total splash time
            return () => clearTimeout(timer);
        }
    }, [loading]);

    useEffect(() => {
        // [NEW] Initialize Social SDKs
        initializeKakao();
        initializeNaver();
    }, []);

    useEffect(() => {
        let isCancelled = false;

        const formatLocalDate = (date) =>
            `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

        const runNotifications = async () => {
            const todayStr = formatLocalDate(new Date());
            const checkedKey = `notifications_checked_${todayStr}`;
            if (localStorage.getItem(checkedKey)) return;

            let latestSchedules = schedules;
            let latestFinances = finances;
            const shouldRefreshScheduleFinance =
                Boolean(user && !user.isAnonymous) &&
                (
                    !["dashboard", "schedule", "finance", "statistics", "ai-assistant"].includes(activeMenu) ||
                    schedules.length === 0 ||
                    finances.length === 0
                );

            if (shouldRefreshScheduleFinance) {
                try {
                    const scopeConstraint = userProfile?.organizationId
                        ? where("organizationId", "==", userProfile.organizationId)
                        : where("userId", "==", user.uid);

                    const [scheduleSnapshot, financeSnapshot] = await Promise.all([
                        getDocs(query(collection(db, "schedules"), scopeConstraint)),
                        getDocs(query(collection(db, "finances"), scopeConstraint)),
                    ]);

                    if (isCancelled) return;

                    latestSchedules = scheduleSnapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }));
                    latestFinances = financeSnapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }));
                } catch (error) {
                    console.error("Auto notification one-shot sync failed:", error);
                }
            }

            runDailyAutoNotifications({
                students,
                schedules: latestSchedules,
                finances: latestFinances,
                annualBudget,
                addNotification
            });
        };

        runNotifications();

        return () => {
            isCancelled = true;
        };
    }, [
        students,
        schedules,
        finances,
        annualBudget,
        addNotification,
        user?.uid,
        user?.isAnonymous,
        userProfile?.organizationId,
        activeMenu,
    ]);

    const handleUpdateProfile = async (newData) => {
        await updateProfileWithMigration({
            db,
            user,
            userProfile,
            newData,
            students,
            teachers,
            updateItem,
            setUserProfile,
            openAlert
        });
    };

    const {
        handleLogoutRequest,
        handleLogoutConfirm,
        handleLogoClick,
        handleNavigateToAttendance,
        handleNavigateToLog,
        handleGuestLogin,
        handleEmailLogin,
        handleResetPassword,
        handleFindId
    } = useMemo(
        () => createBasicHandlers({
            auth,
            db,
            user,
            openAlert,
            setIsLogoutConfirmOpen,
            setActiveMenu,
            setSelectedDepartment,
            setTargetDate,
            setTargetStudentId,
            setTargetLogId,
            cleanupGuestData
        }),
        [user, openAlert]
    );

    const { handleTourFinish } = useMemo(
        () => createTourHandlers({
            db,
            user,
            setUserProfile,
            setShowTour,
            setTourAction
        }),
        [user]
    );

    // [NEW] Handle specific tour actions that affect global UI (like opening Profile Modal)
    useEffect(() => {
        if (tourAction === 'demo_open_profile') {
            setIsProfileModalOpen(true);
        }
        if (tourAction === 'demo_open_journey') {
            setIsProfileModalOpen(true);
        }
    }, [tourAction]);
    const {
        processSocialLogin,
        handleSocialPersistentLogin,
        handleGoogleLogin,
        handleKakaoLogin,
        handleNaverLogin,
        handleSignup,
        handleCancelSocialSignup
    } = useMemo(
        () => createSocialAuthHandlers({
            auth,
            db,
            openAlert,
            pendingSocialData,
            user,
            setUser,
            setUserProfile,
            setPendingSocialData,
            setAppReady,
            setLoading,
            seedDemoData
        }),
        [openAlert, pendingSocialData, user]
    );

    // Listen for Naver Popup
    useEffect(() => {
        const handleMessage = createNaverMessageHandler({
            handleSocialPersistentLogin,
            processSocialLogin,
            setLoading,
            openAlert
        });
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [handleSocialPersistentLogin, processSocialLogin, openAlert]);

    // Also check for Redirect (Callback) on mount
    useEffect(() => {
        runNaverRedirectCheck({
            handleNaverCallback,
            handleSocialPersistentLogin,
            processSocialLogin,
            setIsNaverCallback,
            setLoading,
            openAlert
        });
    }, [handleSocialPersistentLogin, processSocialLogin, openAlert]);
    // ... (Logins)

    // ... (Render)
    const mainContentState = useMemo(
        () => ({
            students,
            teachers,
            schedules,
            finances,
            annualBudget,
            userProfile,
            meetingMinutes,
            visitLogs,
            isDashboardSettingsOpen,
            isAaronOpen,
            selectedDepartment,
            myDepartments,
            currentTheme,
            chatHistory,
            earnedBadges,
            level,
            levelProgress,
            totalPoints,
            isMobile,
            attendanceLogs,
            studentListType,
            studentSearchTerm,
            tourAction,
            studentToEdit,
            targetDate,
            attendanceSearchQuery,
            attendanceTargetGroup,
            targetStudentId,
            targetTeacherId,
            targetStatisticsSection,
            detailReturnMenu,
            targetLogId,
            counselingSearchQuery,
            counselingTargetGroup,
            user,
            isStatsEditMode,
            isStatsSettingsOpen
        }),
        [
            students,
            teachers,
            schedules,
            finances,
            annualBudget,
            userProfile,
            meetingMinutes,
            visitLogs,
            isDashboardSettingsOpen,
            isAaronOpen,
            selectedDepartment,
            myDepartments,
            currentTheme,
            chatHistory,
            earnedBadges,
            level,
            levelProgress,
            totalPoints,
            isMobile,
            attendanceLogs,
            studentListType,
            studentSearchTerm,
            tourAction,
            studentToEdit,
            targetDate,
            attendanceSearchQuery,
            attendanceTargetGroup,
            targetStudentId,
            targetTeacherId,
            targetStatisticsSection,
            detailReturnMenu,
            targetLogId,
            counselingSearchQuery,
            counselingTargetGroup,
            user,
            isStatsEditMode,
            isStatsSettingsOpen
        ]
    );

    const mainContentActions = useMemo(
        () => ({
            setActiveMenu,
            addItem,
            updateItem,
            deleteItem,
            bulkAddItem,
            bulkDeleteItem,
            setIsDashboardSettingsOpen,
            handleUpdateMyDepartments,
            setChatHistory,
            setIsProfileModalOpen,
            setStudentListType,
            setStudentSearchTerm,
            handleNavigateToAttendance,
            handleNavigateToLog,
            setStudentToEdit,
            incrementStat,
            handleOpenCheerModal,
            checkAndAwardBadges,
            setTargetDate,
            setAttendanceSearchQuery,
            setAttendanceTargetGroup,
            refreshAttendanceLogs,
            setTargetLogId,
            setCounselingSearchQuery,
            setCounselingTargetGroup,
            setTargetStudentId,
            setTargetTeacherId,
            setTargetStatisticsSection,
            setDetailReturnMenu,
            handleUpdateAnnualBudget,
            setIsStatsEditMode,
            setIsStatsSettingsOpen,
            setIsAaronOpen,
            setSelectedDepartment
        }),
        [
            setActiveMenu,
            addItem,
            updateItem,
            deleteItem,
            bulkAddItem,
            bulkDeleteItem,
            setIsDashboardSettingsOpen,
            handleUpdateMyDepartments,
            setChatHistory,
            setIsProfileModalOpen,
            setStudentListType,
            setStudentSearchTerm,
            handleNavigateToAttendance,
            handleNavigateToLog,
            setStudentToEdit,
            incrementStat,
            handleOpenCheerModal,
            checkAndAwardBadges,
            setTargetDate,
            setAttendanceSearchQuery,
            setAttendanceTargetGroup,
            refreshAttendanceLogs,
            setTargetLogId,
            setCounselingSearchQuery,
            setCounselingTargetGroup,
            setTargetStudentId,
            setTargetTeacherId,
            setTargetStatisticsSection,
            setDetailReturnMenu,
            handleUpdateAnnualBudget,
            setIsStatsEditMode,
            setIsStatsSettingsOpen,
            setIsAaronOpen,
            setSelectedDepartment
        ]
    );

    const renderedContent = useMemo(() => (
        <div key={activeMenu} className="animate-fade-in-page h-full">
            {renderMainContent({
                activeMenu,
                state: mainContentState,
                actions: mainContentActions
            })}
        </div>
    ), [activeMenu, mainContentState, mainContentActions]);

    const centerActionContent = getCenterActionContent({
        activeMenu,
        currentTheme,
        attendanceTargetGroup,
        counselingTargetGroup,
        studentListType,
        setAttendanceTargetGroup,
        setCounselingTargetGroup,
        setStudentListType
    });

    const headerActionContent = getHeaderActionContent({
        activeMenu,
        currentTheme,
        setIsDashboardSettingsOpen,
        setIsStatsSettingsOpen
    });

    const reportStats = useMemo(
        () => buildMinistryReportStats({ students, visitLogs, finances }),
        [students, visitLogs, finances]
    );

    const handleOpenProfile = useCallback(() => {
        setIsProfileModalOpen(true);
    }, []);

    const resolveStudentFromNotificationTarget = useCallback((target) => {
        if (!target) return null;
        if (typeof target === "string") {
            return students.find((student) => student.id === target) || null;
        }
        if (target?.kind === "studentName" && target.value) {
            return students.find((student) => student.name === target.value) || null;
        }
        if (target?.kind === "name" && target.value) {
            return students.find((student) => student.name === target.value) || null;
        }
        return null;
    }, [students]);

    const resolveTeacherFromNotificationTarget = useCallback((target) => {
        if (!target) return null;
        if (typeof target === "string") {
            return teachers.find((teacher) => teacher.id === target) || null;
        }
        if (target?.kind === "teacherName" && target.value) {
            return teachers.find((teacher) => teacher.name === target.value) || null;
        }
        if (target?.kind === "name" && target.value) {
            return teachers.find((teacher) => teacher.name === target.value) || null;
        }
        return null;
    }, [teachers]);

    const buildNotificationDetailItem = useCallback((item, isTeacherItem) => {
        if (!item?.id) return item;
        const myVisits = visitLogs
            .filter((log) => {
                if (!log || !log.date) return false;
                if (isTeacherItem) {
                    return log.teacherId === item.id || log.studentId === item.id;
                }
                return log.studentId === item.id;
            })
            .sort((a, b) => new Date(b.date) - new Date(a.date));
        const lastVisitDate = myVisits.length > 0 ? myVisits[0].date : "-";

        const myAttendances = attendanceLogs
            .filter((log) => log && (log[item.id] === "present" || log[item.id] === "late"))
            .map((log) => log.date)
            .sort((a, b) => new Date(b) - new Date(a));
        const lastAttendanceDate = myAttendances.length > 0 ? myAttendances[0] : "-";

        return {
            ...item,
            lastVisit: lastVisitDate,
            lastAttendance: lastAttendanceDate,
        };
    }, [attendanceLogs, visitLogs]);

    const handleNotificationTouchStart = useCallback((e) => {
        setNotificationTouchEnd(null);
        setNotificationTouchEndY(null);
        setNotificationTouchStart(e.targetTouches[0].clientX);
        setNotificationTouchStartY(e.targetTouches[0].clientY);
    }, []);

    const handleNotificationTouchMove = useCallback((e) => {
        setNotificationTouchEnd(e.targetTouches[0].clientX);
        setNotificationTouchEndY(e.targetTouches[0].clientY);
    }, []);

    const handleNotificationModalSwipeEnd = useCallback((closeHandler) => {
        if (
            notificationTouchStart == null ||
            notificationTouchEnd == null ||
            notificationTouchStartY == null ||
            notificationTouchEndY == null
        ) return;

        const horizontalDistance = notificationTouchStart - notificationTouchEnd;
        const verticalDistance = Math.abs(notificationTouchStartY - notificationTouchEndY);
        const isHorizontalIntent = Math.abs(horizontalDistance) > 200 && verticalDistance < 80;
        if (!isHorizontalIntent) return;

        if (horizontalDistance < 0) {
            closeHandler();
        }
    }, [notificationTouchEnd, notificationTouchEndY, notificationTouchStart, notificationTouchStartY]);

    const resetNotificationDetailState = useCallback(() => {
        setNotificationTeacherLogInputContent("");
        setNotificationTeacherLogInputType("면담");
        setNotificationTeacherLogInputDate(new Date().toISOString().split('T')[0]);
        setNotificationTeacherLogInputSentiment("neutral");
        setNotificationShowAllLogs(false);
        setNotificationEditingLogId(null);
        setIsNotificationCreatingLog(false);
        setIsNotificationDetailEditing(false);
        setIsNotificationDetailDeptOpen(false);
        setNotificationShowLinkPopup(false);
        setNotificationTeamMembers([]);
        setNotificationLinkLoading(false);
        setNotificationTouchStart(null);
        setNotificationTouchStartY(null);
        setNotificationTouchEnd(null);
        setNotificationTouchEndY(null);
    }, []);

    const handleOpenNotificationMemberDetail = useCallback((menu, target) => {
        if (menu === "students") {
            const targetStudent = resolveStudentFromNotificationTarget(target);
            if (!targetStudent) {
                openAlert("대상 학생 정보를 찾을 수 없습니다.", "알림");
                return;
            }
            const enrichedStudent = buildNotificationDetailItem(targetStudent, false);
            resetNotificationDetailState();
            setNotificationTeacherDetail(null);
            setNotificationStudentDetail(enrichedStudent);
            setNotificationFormData(enrichedStudent);
            setNotificationTeacherFormData(createEmptyNotificationTeacherForm());
            return;
        }

        if (menu === "teachers") {
            const targetTeacher = resolveTeacherFromNotificationTarget(target);
            if (!targetTeacher) {
                openAlert("대상 교사 정보를 찾을 수 없습니다.", "알림");
                return;
            }
            const enrichedTeacher = buildNotificationDetailItem(targetTeacher, true);
            resetNotificationDetailState();
            setNotificationStudentDetail(null);
            setNotificationTeacherDetail(enrichedTeacher);
            setNotificationTeacherFormData(enrichedTeacher);
            setNotificationFormData(createEmptyNotificationStudentForm());
            return;
        }

        if (menu === "member") {
            const targetStudent = resolveStudentFromNotificationTarget(target);
            if (targetStudent) {
                const enrichedStudent = buildNotificationDetailItem(targetStudent, false);
                resetNotificationDetailState();
                setNotificationTeacherDetail(null);
                setNotificationStudentDetail(enrichedStudent);
                setNotificationFormData(enrichedStudent);
                setNotificationTeacherFormData(createEmptyNotificationTeacherForm());
                return;
            }

            const targetTeacher = resolveTeacherFromNotificationTarget(target);
            if (targetTeacher) {
                const enrichedTeacher = buildNotificationDetailItem(targetTeacher, true);
                resetNotificationDetailState();
                setNotificationStudentDetail(null);
                setNotificationTeacherDetail(enrichedTeacher);
                setNotificationTeacherFormData(enrichedTeacher);
                setNotificationFormData(createEmptyNotificationStudentForm());
                return;
            }

            openAlert("대상 상세 정보를 찾을 수 없습니다.", "알림");
        }
    }, [buildNotificationDetailItem, openAlert, resetNotificationDetailState, resolveStudentFromNotificationTarget, resolveTeacherFromNotificationTarget]);

    const closeNotificationMemberDetail = useCallback(() => {
        setNotificationStudentDetail(null);
        setNotificationTeacherDetail(null);
        setNotificationFormData(createEmptyNotificationStudentForm());
        setNotificationTeacherFormData(createEmptyNotificationTeacherForm());
        resetNotificationDetailState();
    }, [resetNotificationDetailState]);

    const handleNotificationStudentShare = useCallback((student) => {
        const text = `[학생 정보]\n이름: ${student.name} (${student.gender || "-"})\n연락처: ${student.phone || "-"}\n부서: ${student.department || student.grade || "-"}\n학교/직장: ${student.school || "-"}\n주소: ${student.address || "-"}\n신앙상태: ${student.faithState || "-"}\n비고: ${student.notes || "-"}`;
        navigator.clipboard
            .writeText(text)
            .then(() => openAlert("학생 정보가 복사되었습니다!", "알림"))
            .catch(() => openAlert("복사에 실패했습니다.", "오류"));
    }, [openAlert]);

    const handleNotificationTeacherShare = useCallback((teacher) => {
        const text = `[교사 정보]\n이름: ${teacher.name}\n직분: ${teacher.role || "-"}\n연락처: ${teacher.phone || "-"}\n담당: ${teacher.charge || "-"}\n비고: ${teacher.notes || "-"}`;
        navigator.clipboard
            .writeText(text)
            .then(() => openAlert("교사 정보가 복사되었습니다!", "알림"))
            .catch(() => openAlert("복사에 실패했습니다.", "오류"));
    }, [openAlert]);

    const notificationDepartmentOptions = useMemo(
        () => buildDepartmentOptions({ myDepartments, userProfile }),
        [myDepartments, userProfile]
    );

    const notificationMobileViewingItem = useMemo(() => {
        if (notificationTeacherDetail) {
            return { ...notificationTeacherDetail, isTeacher: true };
        }
        if (notificationStudentDetail) {
            return { ...notificationStudentDetail, isTeacher: false };
        }
        return null;
    }, [notificationTeacherDetail, notificationStudentDetail]);

    const handleNotificationDetailEditingChange = useCallback((next) => {
        setIsNotificationDetailEditing(Boolean(next));
    }, []);

    const handleNotificationDetailSave = useCallback((e) => {
        e?.preventDefault?.();

        if (notificationTeacherDetail) {
            const updatedTeacher = {
                ...notificationTeacherFormData,
                id: notificationTeacherDetail.id,
            };
            updateItem("teachers", notificationTeacherDetail.id, updatedTeacher);
            const enrichedTeacher = buildNotificationDetailItem(updatedTeacher, true);
            setNotificationTeacherDetail(enrichedTeacher);
            setNotificationTeacherFormData(enrichedTeacher);
            setIsNotificationDetailEditing(false);
            return;
        }

        if (notificationStudentDetail) {
            const updatedStudent = {
                ...notificationFormData,
                id: notificationStudentDetail.id,
            };
            updateItem("students", notificationStudentDetail.id, updatedStudent);
            const enrichedStudent = buildNotificationDetailItem(updatedStudent, false);
            setNotificationStudentDetail(enrichedStudent);
            setNotificationFormData(enrichedStudent);
            setIsNotificationDetailEditing(false);
        }
    }, [
        buildNotificationDetailItem,
        notificationFormData,
        notificationStudentDetail,
        notificationTeacherDetail,
        notificationTeacherFormData,
        updateItem,
    ]);

    const handleNotificationDetailDelete = useCallback(() => {
        const viewingItem = notificationTeacherDetail || notificationStudentDetail;
        if (!viewingItem) return;

        openConfirm(
            "사역 기록을 정리할까요?",
            "한 번 삭제하면 복구할 수 없습니다. 정말 보내드려야 할까요? 🥲",
            () => {
                if (notificationTeacherDetail) {
                    deleteItem("teachers", viewingItem.id);
                } else {
                    deleteItem("students", viewingItem.id);
                }
                closeNotificationMemberDetail();
            }
        );
    }, [closeNotificationMemberDetail, deleteItem, notificationStudentDetail, notificationTeacherDetail, openConfirm]);

    const handleNotificationDetailImageUpload = useCallback(async (e) => {
        const file = e?.target?.files?.[0];
        if (!file) return;

        const isTeacherDetail = Boolean(notificationTeacherDetail);
        setIsNotificationUploading(true);
        try {
            const storageRef = ref(
                storage,
                `${isTeacherDetail ? "teachers" : "students"}/${Date.now()}_${file.name}`
            );
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            if (isTeacherDetail) {
                setNotificationTeacherFormData((prev) => ({ ...prev, photoUrl: url }));
            } else {
                setNotificationFormData((prev) => ({ ...prev, photoUrl: url }));
            }
        } catch (error) {
            console.error("Notification detail image upload failed:", error);
            openAlert("이미지 업로드 실패", "오류");
        } finally {
            setIsNotificationUploading(false);
        }
    }, [notificationTeacherDetail, openAlert]);

    const fetchNotificationTeamMembers = useCallback(async () => {
        if (!userProfile?.organizationId) return;
        setNotificationLinkLoading(true);
        try {
            const q = query(
                collection(db, "users"),
                where("organizationId", "==", userProfile.organizationId)
            );
            const snapshot = await getDocs(q);
            const members = snapshot.docs.map((docItem) => ({ uid: docItem.id, ...docItem.data() }));
            setNotificationTeamMembers(members);
        } catch (error) {
            console.error("알림 상세 팀 멤버 조회 오류:", error);
        } finally {
            setNotificationLinkLoading(false);
        }
    }, [userProfile?.organizationId]);

    const handleNotificationLinkAccount = useCallback(async (memberUid, memberName) => {
        if (!notificationTeacherDetail) return;
        try {
            const updatedTeacher = {
                ...notificationTeacherDetail,
                linkedUserId: memberUid,
                linkedUserName: memberName,
            };
            await updateItem("teachers", notificationTeacherDetail.id, updatedTeacher);
            setNotificationTeacherDetail(updatedTeacher);
            setNotificationTeacherFormData(updatedTeacher);
            setNotificationShowLinkPopup(false);
            openAlert(
                `${notificationTeacherDetail.name} 교사가 ${memberName}님의 계정과 연결되었습니다.`,
                "계정 연결 완료"
            );
        } catch (error) {
            console.error("알림 상세 계정 연결 오류:", error);
        }
    }, [notificationTeacherDetail, openAlert, updateItem]);

    const handleNotificationUnlinkAccount = useCallback(() => {
        if (!notificationTeacherDetail) return;
        openConfirm(
            "계정 연결 해제",
            `${notificationTeacherDetail.name} 교사의 계정 연결을 해제하시겠습니까?`,
            async () => {
                try {
                    const updatedTeacher = {
                        ...notificationTeacherDetail,
                        linkedUserId: null,
                        linkedUserName: null,
                    };
                    await updateItem("teachers", notificationTeacherDetail.id, updatedTeacher);
                    setNotificationTeacherDetail(updatedTeacher);
                    setNotificationTeacherFormData(updatedTeacher);
                    openAlert("계정 연결이 해제되었습니다.", "연결 해제 완료");
                } catch (error) {
                    console.error("알림 상세 계정 연결 해제 오류:", error);
                }
            }
        );
    }, [notificationTeacherDetail, openAlert, openConfirm, updateItem]);

    const shouldMaskTeacherInfoForNotification = useCallback(
        (teacherItem) =>
            shouldMaskTeacherInfoMobile({
                teacherItem,
                userProfile,
                isTeacherList: true,
                currentUserUid: auth.currentUser?.uid,
            }),
        [userProfile]
    );

    const handleHeaderNavigate = useCallback((menu, targetId = null) => {
        const isMemberDetailDeepLink = (menu === 'students' || menu === 'teachers') && Boolean(targetId) && activeMenu !== menu;
        if (isMemberDetailDeepLink) {
            setDetailReturnMenu(activeMenu);
        }

        if (menu === 'students') {
            setStudentListType('student');
            setTargetStudentId(targetId || null);
            setTargetTeacherId(null);
            if (!targetId) setDetailReturnMenu(null);
        } else if (menu === 'teachers') {
            setTargetStudentId(null);
            setTargetTeacherId(targetId || null);
            if (!targetId) setDetailReturnMenu(null);
        } else {
            setTargetStudentId(null);
            setTargetTeacherId(null);
            setDetailReturnMenu(null);
        }
        setActiveMenu(menu);
    }, [activeMenu]);

    const handleCloseMobileSidebar = useCallback(() => {
        setIsMobileMenuOpen(false);
    }, []);

    const sharedSidebarProps = useMemo(
        () => ({
            activeMenu,
            setActiveMenu,
            isMobileMenuOpen,
            setIsMobileMenuOpen,
            deptName: userProfile?.deptName || "",
            handleEditDeptName: handleUpdateProfile,
            user,
            userProfile,
            handleLogout: handleLogoutRequest,
            onOpenProfile: handleOpenProfile,
            myDepartments,
            selectedDepartment,
            setSelectedDepartment,
            theme: currentTheme
        }),
        [
            activeMenu,
            setActiveMenu,
            isMobileMenuOpen,
            setIsMobileMenuOpen,
            userProfile,
            handleUpdateProfile,
            user,
            handleLogoutRequest,
            handleOpenProfile,
            myDepartments,
            selectedDepartment,
            setSelectedDepartment,
            currentTheme
        ]
    );

    // [FIX] 네이버 콜백 처리 중일 때는 UI 렌더링 차단하고 로딩 화면만 표시
    // 이렇게 해야 handleNaverCallback이 백그라운드에서 안전하게 돌고 창을 닫을 수 있음
    if (isNaverCallback) {
        return <NaverCallbackLoading />;
    }

    return (
        <div className={`min-h-screen bg-[#F0F0EB] text-gray-900 font-sans transition-colors duration-300`}>
            <SplashScreen isVisible={showSplash} />

            {/* Main App Content - Hidden while initializing to prevent FOUC, but loaded for speed */}
            <div className={`transition-opacity duration-1000 ${showSplash ? 'opacity-0' : 'opacity-100'}`}>
                {/* 1. 로그인 전 OR 소셜 추가정보 입력 중 */}
                {(!user || pendingSocialData) ? (
                    loading ? null : <Login
                        handleGoogleLogin={handleGoogleLogin}
                        handleGuestLogin={handleGuestLogin}
                        handleEmailLogin={handleEmailLogin}
                        handleSignup={handleSignup}
                        handleResetPassword={handleResetPassword}
                        handleFindId={handleFindId}
                        handleKakaoLogin={handleKakaoLogin}
                        handleNaverLogin={handleNaverLogin}
                        pendingSocialData={pendingSocialData}
                        onCancelSocialSignup={handleCancelSocialSignup}
                    />
                ) : !userProfile?.setupComplete && !user?.isAnonymous ? (
                    // 1.5. 조직 설정 필요 (Organization Setup)
                    <OrganizationSetup
                        user={user}
                        userProfile={userProfile}
                        onComplete={() => {
                            // Reload profile after setup
                            window.location.reload();
                        }}
                    />
                ) : (
                    // 2. 로그인 후 (Main Layout)
                    <div className="flex h-screen overflow-hidden relative">


                        {/* Desktop Sidebar */}
                        <div className="hidden md:flex h-full">
                            <Sidebar {...sharedSidebarProps} />
                        </div>

                        <MobileSidebarPortal
                            isOpen={isMobileMenuOpen}
                            onClose={handleCloseMobileSidebar}
                            onTouchStart={onTouchStart}
                            onTouchMove={onTouchMove}
                            onTouchEnd={onMenuSwipeEnd}
                            sidebarProps={sharedSidebarProps}
                        />

                        {/* Mobile Bottom Navigation */}
                        <BottomNav
                            activeMenu={activeMenu}
                            setActiveMenu={setActiveMenu}
                            onOpenMenu={() => setIsMobileMenuOpen(true)}
                        />

                        {/* ai-assistant, students, teachers, attendance, dashboard는 자체적으로 BottomNav 높이 관리 */}
                        <div className={`relative z-10 flex w-full h-full md:pb-0 ${getBottomPaddingClass(activeMenu)}`}>
                            <main className="flex-1 flex flex-col h-full overflow-hidden bg-transparent w-full relative md:pt-0" style={{ paddingTop: getMainPaddingTop(activeMenu) }}>
                                {activeMenu !== 'ai-assistant' && (
                                    <Header
                                        activeMenu={activeMenu}
                                        setIsMobileMenuOpen={setIsMobileMenuOpen}
                                        user={user}
                                        onLogoClick={handleLogoClick}
                                        selectedDepartment={selectedDepartment}
                                        setSelectedDepartment={setSelectedDepartment}
                                        myDepartments={myDepartments}
                                        deptName={userProfile?.deptName || ""}
                                        theme={currentTheme}
                                        userProfile={userProfile}
                                        centerAction={centerActionContent}
                                        headerAction={headerActionContent}
                                        onNavigate={handleHeaderNavigate}
                                        onOpenMemberDetail={handleOpenNotificationMemberDetail}
                                    />
                                )}
                                {/* [NEW] Dashboard Report Modal */}
                                {activeMenu === 'dashboard' && (
                                    <MinistryReportModal
                                        isOpen={isReportOpen}
                                        onClose={() => setIsReportOpen(false)}
                                        deptName={selectedDepartment === 'All' ? '전체 부서' : selectedDepartment}
                                        theme={currentTheme}
                                        stats={reportStats}
                                    />
                                )}
                                {/* ai-assistant, students, teachers, attendance는 자체 스크롤 관리 → 모바일에서만 overflow-hidden, 데스크톱은 정상 스크롤 */}
                                <div className={getContentContainerClass(activeMenu)}>
                                    <ErrorBoundary>
                                        {renderedContent}
                                    </ErrorBoundary>
                                </div>
                            </main>
                            <ProfileEditModal
                                isOpen={isProfileModalOpen}
                                onClose={() => setIsProfileModalOpen(false)}
                                userProfile={userProfile}
                                onUpdate={handleUpdateProfile}
                                myDepartments={myDepartments}
                                onUpdateDepartments={handleUpdateMyDepartments}
                                earnedBadges={earnedBadges}
                                totalPoints={totalPoints}
                                level={level}
                                levelProgress={levelProgress}
                            />
                            {isMobile ? (
                                <MobileDetailView
                                    viewingItem={notificationMobileViewingItem}
                                    setViewingItem={() => { }}
                                    onCloseDetail={closeNotificationMemberDetail}
                                    isTeacher={Boolean(notificationTeacherDetail)}
                                    isDetailEditing={isNotificationDetailEditing}
                                    setIsDetailEditing={handleNotificationDetailEditingChange}
                                    teacherFormData={notificationTeacherFormData}
                                    setTeacherFormData={setNotificationTeacherFormData}
                                    formData={notificationFormData}
                                    setFormData={setNotificationFormData}
                                    departmentOptions={notificationDepartmentOptions}
                                    isDetailDeptOpen={isNotificationDetailDeptOpen}
                                    setIsDetailDeptOpen={setIsNotificationDetailDeptOpen}
                                    handleDetailSave={handleNotificationDetailSave}
                                    handleDelete={handleNotificationDetailDelete}
                                    handleImageUpload={handleNotificationDetailImageUpload}
                                    onTouchStart={handleNotificationTouchStart}
                                    onTouchMove={handleNotificationTouchMove}
                                    onModalSwipeEnd={handleNotificationModalSwipeEnd}
                                    getDeptColorClass={(deptName) => getDeptColorClass(deptName, userProfile)}
                                    shouldMaskTeacherInfo={shouldMaskTeacherInfoForNotification}
                                    showLinkPopup={notificationShowLinkPopup}
                                    setShowLinkPopup={setNotificationShowLinkPopup}
                                    teamMembers={notificationTeamMembers}
                                    linkLoading={notificationLinkLoading}
                                    fetchTeamMembers={fetchNotificationTeamMembers}
                                    handleLinkAccount={handleNotificationLinkAccount}
                                    handleUnlinkAccount={handleNotificationUnlinkAccount}
                                    onOpenCheerModal={handleOpenCheerModal}
                                    userProfile={userProfile}
                                    logInputType={notificationTeacherLogInputType}
                                    setLogInputType={setNotificationTeacherLogInputType}
                                    logInputContent={notificationTeacherLogInputContent}
                                    setLogInputContent={setNotificationTeacherLogInputContent}
                                    logInputDate={notificationTeacherLogInputDate}
                                    setLogInputDate={setNotificationTeacherLogInputDate}
                                    logInputSentiment={notificationTeacherLogInputSentiment}
                                    setLogInputSentiment={setNotificationTeacherLogInputSentiment}
                                    editingLogId={notificationEditingLogId}
                                    setEditingLogId={setNotificationEditingLogId}
                                    setIsCreatingLog={setIsNotificationCreatingLog}
                                    showAllLogs={notificationShowAllLogs}
                                    setShowAllLogs={setNotificationShowAllLogs}
                                    visitLogs={visitLogs}
                                    attendanceLogs={attendanceLogs}
                                    onUpdateLog={(d) => updateItem("visitLogs", d.id, d)}
                                    onDeleteLog={(id) => deleteItem("visitLogs", id)}
                                    onAddLog={(d) => addItem("visitLogs", d)}
                                    openAlert={openAlert}
                                    openConfirm={openConfirm}
                                    onNavigateToAttendance={handleNavigateToAttendance}
                                    icons={MOBILE_LIST_ICONS}
                                />
                            ) : (
                                <>
                                    <StudentDetailModal
                                        viewingStudent={notificationStudentDetail}
                                        detailModalPos={{ x: 0, y: 0 }}
                                        handleDetailMouseDown={() => { }}
                                        onClose={closeNotificationMemberDetail}
                                        visitLogs={visitLogs}
                                        attendanceLogs={attendanceLogs}
                                        icons={STUDENT_ICONS}
                                        theme={currentTheme}
                                        faithStateStyles={STUDENT_FAITH_STATE_STYLES}
                                        onOpenLog={() => openAlert("심방 상세는 학생 명단에서 확인할 수 있습니다.", "알림")}
                                        isTeacher={true}
                                        onShare={handleNotificationStudentShare}
                                        onEdit={() => openAlert("알림 상세에서는 수정할 수 없습니다.", "알림")}
                                        onDelete={() => openAlert("알림 상세에서는 삭제할 수 없습니다.", "알림")}
                                    />
                                    <TeacherDetailModal
                                        attendanceLogs={attendanceLogs}
                                        detailModalPos={notificationTeacherDetailPos}
                                        editingLogId={null}
                                        fetchTeamMembers={() => { }}
                                        handleDeleteTeacher={() => openAlert("알림 상세에서는 삭제할 수 없습니다.", "알림")}
                                        handleDetailMouseDown={() => { }}
                                        handleLinkAccount={() => { }}
                                        handleShareTeacher={handleNotificationTeacherShare}
                                        handleUnlinkAccount={() => { }}
                                        isMobile={false}
                                        isTeacher={true}
                                        linkLoading={false}
                                        logInputContent={notificationTeacherLogInputContent}
                                        logInputDate={notificationTeacherLogInputDate}
                                        logInputSentiment={notificationTeacherLogInputSentiment}
                                        logInputType={notificationTeacherLogInputType}
                                        onAddLog={() => { }}
                                        onClose={closeNotificationMemberDetail}
                                        onEdit={() => openAlert("알림 상세에서는 수정할 수 없습니다.", "알림")}
                                        onOpenCheerModal={handleOpenCheerModal}
                                        onUpdateLog={() => { }}
                                        openAlert={openAlert}
                                        setEditingLogId={() => { }}
                                        setLogInputContent={setNotificationTeacherLogInputContent}
                                        setLogInputDate={setNotificationTeacherLogInputDate}
                                        setLogInputSentiment={setNotificationTeacherLogInputSentiment}
                                        setLogInputType={setNotificationTeacherLogInputType}
                                        setShowLinkPopup={() => { }}
                                        showLinkPopup={false}
                                        teamMembers={[]}
                                        theme={currentTheme}
                                        userProfile={userProfile}
                                        visitLogs={visitLogs}
                                        viewingTeacher={notificationTeacherDetail}
                                    />
                                </>
                            )}
                            {/* [MOD] Aaron Widget - Only for Admin role */}
                            {userProfile?.memberRole !== 'teacher' && (
                                <div className="hidden md:block">
                                    <AaronWidget
                                        user={user}
                                        students={students}
                                        teachers={teachers}
                                        visitLogs={visitLogs}
                                        finances={finances}
                                        schedules={schedules}
                                        attendanceLogs={attendanceLogs}
                                        chatHistory={chatHistory}
                                        setChatHistory={setChatHistory}
                                        onNavigate={() => setActiveMenu('ai-assistant')}
                                        isOpen={isAaronOpen}
                                        onToggle={() => setIsAaronOpen(prev => !prev)}
                                        onClose={() => setIsAaronOpen(false)}
                                        selectedDepartment={selectedDepartment}
                                        theme={currentTheme}
                                    />
                                </div>
                            )}

                            <LogoutConfirmModal
                                isOpen={isLogoutConfirmOpen}
                                user={user}
                                onClose={() => setIsLogoutConfirmOpen(false)}
                                onConfirm={handleLogoutConfirm}
                            />
                        </div>

                        {/* Product Tour Overlay */}
                        {showTour && (
                            <ProductTour
                                onFinish={handleTourFinish}
                                setActiveMenu={setActiveMenu}
                                userProfile={userProfile}
                                onStepChange={setTourAction}
                            />
                        )}
                    </div>
                )
                }
            </div >

            {/* 🏆 배지 획득 모달 */}
            {showBadgeModal && newBadge && (
                <BadgeModal badge={newBadge} onClose={closeBadgeModal} />
            )}

            {/* 💝 격려 보내기 모달 */}
            <CheerModal
                isOpen={isCheerModalOpen}
                onClose={() => { setIsCheerModalOpen(false); setCheerTargetUser(null); }}
                targetUser={cheerTargetUser}
                onSendCheer={handleSendCheer}
            />

            {/* [NEW] 격려 받음 알림 모달 */}
            <CheerReceivedModal
                cheer={currentReceivedCheer}
                onClose={handleCloseCheerReceivedModal}
                onMarkAsRead={markAsRead}
            />
        </div >
    );
}
